import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { site } from '../../site';
import { Wordmark } from '../Wordmark';
import { GetAppButton } from './GetAppButton';

const NAV: { to: string; label: string; hash?: boolean }[] = [
  { to: '/#services', label: 'Services', hash: true },
  { to: '/support', label: 'Support' },
  { to: '/contact', label: 'Contact' },
  { to: '/privacy', label: 'Privacy' },
];

export function MarketingLayout() {
  const { pathname } = useLocation();
  const onInk = pathname === '/';
  const [open, setOpen] = useState(false);

  return (
    <div
      className={
        'flex min-h-screen flex-col ' +
        (onInk ? 'bg-ink text-white' : 'bg-paper text-ink')
      }
    >
      <header
        className={
          'sticky top-0 z-40 ' +
          (onInk ? 'bg-ink/95 backdrop-blur-md' : 'bg-paper/95 backdrop-blur-md')
        }
      >
        <div className="mx-auto flex max-w-6xl items-center gap-6 px-5 py-4 md:px-8">
          <Wordmark tone={onInk ? 'onInk' : 'ink'} size="sm" />
          <nav className="ml-auto hidden items-center gap-7 md:flex">
            {NAV.map((item) =>
              item.hash ? (
                <Link
                  key={item.to}
                  to={item.to}
                  className={
                    'text-sm font-bold ' +
                    (onInk
                      ? 'text-white/70 hover:text-white'
                      : 'text-ink/50 hover:text-ink')
                  }
                >
                  {item.label}
                </Link>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    'text-sm font-bold ' +
                    (isActive
                      ? onInk
                        ? 'text-mango'
                        : 'text-ink'
                      : onInk
                        ? 'text-white/70 hover:text-white'
                        : 'text-ink/50 hover:text-ink')
                  }
                >
                  {item.label}
                </NavLink>
              ),
            )}
            <GetAppButton className="btn-primary !px-4 !py-2.5 text-sm" />
          </nav>
          <button
            type="button"
            className={
              'ml-auto inline-flex h-10 w-10 items-center justify-center rounded-tile md:hidden ' +
              (onInk ? 'bg-white/10 text-white' : 'bg-ink/[0.05] text-ink')
            }
            aria-label={open ? 'Close menu' : 'Open menu'}
            onClick={() => setOpen((v) => !v)}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              {open ? (
                <path
                  strokeLinecap="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeWidth={2}
                  d="M4 8h16M4 16h16"
                />
              )}
            </svg>
          </button>
        </div>
        {open && (
          <div
            className={
              'border-t px-5 py-4 md:hidden ' +
              (onInk ? 'border-white/10' : 'border-ink/10')
            }
          >
            <div className="flex flex-col gap-3">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className="text-sm font-bold"
                >
                  {item.label}
                </Link>
              ))}
              <GetAppButton className="btn-primary mt-2 text-center" />
            </div>
          </div>
        )}
      </header>

      <div className="flex-1">
        <Outlet />
      </div>

      <footer className="bg-ink text-white">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 md:grid-cols-4 md:px-8">
          <div className="md:col-span-2">
            <Wordmark tone="onInk" size="sm" />
            <div className="mt-3 h-1 w-8 rounded-full bg-mango" />
            <p className="mt-4 max-w-sm text-sm font-medium leading-relaxed text-white/55">
              {site.tagline}. One app for {site.region}, run by people from
              here.
            </p>
          </div>
          <div>
            <div className="overline !text-white/35">App</div>
            <ul className="mt-3 space-y-2 text-sm font-bold">
              <li>
                <GetAppButton className="text-white/80 hover:text-white">
                  Get the app
                </GetAppButton>
              </li>
              <li>
                <Link to="/#services" className="text-white/80 hover:text-white">
                  Services
                </Link>
              </li>
              <li>
                <a
                  href={site.facebookUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-white/80 hover:text-white"
                >
                  Facebook
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="overline !text-white/35">Help</div>
            <ul className="mt-3 space-y-2 text-sm font-bold">
              <li>
                <Link to="/contact" className="text-white/80 hover:text-white">
                  Contact us
                </Link>
              </li>
              <li>
                <Link to="/support" className="text-white/80 hover:text-white">
                  Support
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="text-white/80 hover:text-white">
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-white/80 hover:text-white">
                  Terms of service
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="mx-auto flex max-w-6xl flex-col gap-2 px-5 py-5 text-xs font-semibold text-white/40 md:flex-row md:px-8">
            <span>
              © {new Date().getFullYear()} {site.operator}. {site.region},{' '}
              {site.country}.
            </span>
            <span className="md:ml-auto">Cash on delivery. No card needed.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
